/** Browser-only popup helper for the per-user Google connect flow. */
export type GoogleConnectorId = "google_calendar" | "google_mail";

/** Turn Google's raw OAuth failure code into copy a user can act on. */
function describeOAuthFailure(error?: string | null, description?: string | null) {
  if (error === "access_denied") {
    return "Google blocked this account. The Pyng Google app is still in testing — your email has to be on its tester list before Live Context can connect.";
  }
  if (error === "admin_policy_enforced") {
    return "Your Google Workspace admin blocked this app. Ask them to allow Pyng, then try again.";
  }
  if (error === "exchange_failed") return "Google approved the connection, but Pyng couldn't finish saving it.";
  if (error === "missing_code") return "Google finished without returning a connection — try again.";
  return description || error || "Google connection failed.";
}

function waitForOAuthCompletion(popup: Window, connectorId: GoogleConnectorId) {
  return new Promise<string | null>((resolve, reject) => {
    let poll: number | undefined;
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (poll !== undefined) window.clearInterval(poll);
    };
    const onMessage = (event: MessageEvent) => {
      const type = event.data?.type;
      if (
        event.origin !== window.location.origin ||
        event.source !== popup ||
        event.data?.connectorId !== connectorId ||
        (type !== "appUserConnectorOAuthComplete" && type !== "appUserConnectorOAuthFailed")
      )
        return;
      cleanup();
      if (type === "appUserConnectorOAuthComplete") {
        resolve(typeof event.data?.code === "string" ? event.data.code : null);
        return;
      }
      popup.close();
      reject(new Error(describeOAuthFailure(event.data?.error, event.data?.errorDescription)));
    };
    window.addEventListener("message", onMessage);
    poll = window.setInterval(() => {
      if (!popup.closed) return;
      cleanup();
      reject(new Error("Window closed before the connection finished."));
    }, 500);
  });
}

export async function runGoogleConnect(
  connectorId: GoogleConnectorId,
  start: (args: {
    data: { connectorId: GoogleConnectorId; switchAccount?: boolean };
  }) => Promise<{ authorizationUrl: string }>,
  complete: (args: { data: { code: string } }) => Promise<unknown>,
  options?: { switchAccount?: boolean },
) {
  const popup = window.open("", "pyng-google-oauth", "width=600,height=720");
  if (!popup) throw new Error("Popup blocked. Allow popups and try again.");
  let code: string | null;
  try {
    const { authorizationUrl } = await start({
      data: { connectorId, switchAccount: options?.switchAccount || undefined },
    });
    const completion = waitForOAuthCompletion(popup, connectorId);
    popup.location.href = authorizationUrl;
    code = await completion;
  } catch (error) {
    popup.close();
    throw error;
  }
  if (code) {
    try {
      await complete({ data: { code } });
    } catch {
      throw new Error(describeOAuthFailure("exchange_failed"));
    }
  }
}
